import AccordionDetails from "@mui/material/AccordionDetails";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Grid,
  FormControl,
  TextField,
  Checkbox,
  Button,
  InputLabel,
} from "@mui/material";
import "./shortageInvestigation.css";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ASInvestigationInitValue,
  ASInvestigationSchema,
} from "../../../schema/asInvestigation";
import { FieldArray, FormikErrors, FormikProvider, useFormik } from "formik";
import dayjs from "dayjs";
import {
  AS_INVESTIGATION_WIZARD_STEPS,
  AS_ALL_INVESTIGATION_FIELDS_GROUPED,
  EFormAction,
  EUploadKey,
  ErrorMessages,
  EShortageInvestigationStatus,
  EASInvestigationFieldsErrorMessage,
  FORM_STYLES,
  FREIGHT_LOCATED_STEP_MAP,
  INVESTIGATION_DONE_STEP_MAP,
  MAX_FILE_SIZE,
  MAX_LENGTH_SUMMARY_TEXT,
  STEP_DAY_MAPPING,
  STEP_STATUS_MAP,
  STEPS_REQUIRED_MAP,
  L_GRID_PROPS,
  M_GRID_PROPS,
  S_GRID_PROPS,
  XL_GRID_PROPS,
  EOsdType,
} from "../../../constants";
import { ShortageInvestigationCreateUpdateAPI } from "../../../API/shortageInvestigationCreateUpdate";
import { useDispatch, useSelector } from "react-redux";
import { PopoutNotification } from "../../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../../AppState/NotificationType";
import { isFormValueChanged } from "../../../AppState/isFormValueChanged";
import Loader from "../../../components/loader/loader";
import {
  chunkArray,
  combineTowJsonObject,
  compareObjects,
  handleKeyDown,
  handleNumberMaxLength,
  handleTextFieldKeyDown,
  handlePaste,
  htmlToImage,
  trimStrings,
} from "../../../helpers";
import {
  IAllShortageInvestigation,
  IDay5Investigation,
} from "../../../models/shortage";
import { debounce } from "lodash";
import mergeWith from "lodash/mergeWith";
import get from "lodash/get";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import ASInvestigationTemplate from "../../../templates/asInvestigationTemplate";
import { FileUploadAPI } from "../../../API/fileUpload";
import { IUploadFile } from "../../../models/master";
import TooltipWithClickAway from "../../../components/terminalPicker/TooltipWithClickAway";
import CustomDialog from "../../../components/confirmationPopup/confirmationPopup";
import { ICallSameDaysDataset } from "../../../models/dsException";
import ASDay4Template from "../../../templates/asDay4Template";
import { hasCompleteShortagesPermissionSelector } from "../../../AppState/selectors/userPermissionsSelectors";
import Day15Investigation from "../sharedShortageComponent/Day15Investigation";
import { InvestigationStepper } from "../sharedShortageComponent/InvestigationStepper";
import NoteBox from "../../../components/noteBox/NoteBox";

type Props = {
  isFormEditMode?: boolean;
  setInvestigationStatus: (status: number) => void;
  osdDetails?: any;
  isCleared?: boolean;
};

const ShortageInvestigation: React.FC<Props> = ({
  osdDetails,
  isFormEditMode = false,
  setInvestigationStatus,
  isCleared
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const showNotification = useCallback((type: NOTIFICATION_TYPE, message: string) => {
    dispatch(PopoutNotification(type, message, Date.now().toString()));
  }, [dispatch]);

  const allFields = AS_ALL_INVESTIGATION_FIELDS_GROUPED.flat();

  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const [activeStep, setActiveStep] = useState<number>(
    STEP_STATUS_MAP[EShortageInvestigationStatus.exceptionEntry]
  );

  const [pageLoading, setPageLoading] = React.useState<boolean>(false);
  const [formFieldsDisabled, setFormFieldsDisabled] =
    React.useState<boolean>(isFormEditMode);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString()
  );
  const [open, setOpen] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [displayCompleteBtn, setDisplayCompleteBtn] = useState<boolean>(true);
  const [formKey, setFormKey] = useState<string>(Date.now().toString());
  const [isFreightLocated, setIsFreightLocated] = useState<boolean>(false);
  const [status, setStatus] = React.useState<number>(1);
  const [lastStatusNumber, setLastStatusNumber] = React.useState<number>(1);
  const [invalidFields, setInvalidFields] = React.useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false); // State to manage dialog open/close
  const [addButtonDisabled, setAddButtonDisabled] = useState(false);
  const [isEditMode, setIsEditMode] = React.useState<boolean>(isFormEditMode);
  const [proInfoAddButtonDisabled, setProInfoAddButtonDisabled] =
    useState(false);
  const [dateKeys, setDateKeys] = React.useState<{
    dateToDestTerm: string;
    dateLastMovement: string;
  }>({
    dateToDestTerm: Date.now().toString(),
    dateLastMovement: Date.now().toString(),
  });
  const hasCompleteShortagesPermission: boolean = useSelector(
    hasCompleteShortagesPermissionSelector
  );

  const [initValues, setInitValues] = React.useState<IAllShortageInvestigation>(
    mergeWith(
      {},
      ASInvestigationInitValue,
      investigationDetails,
      combineTowJsonObject
    )
  );

  const activeDay =
    STEP_DAY_MAPPING[activeStep as keyof typeof STEP_DAY_MAPPING];

  const adjustedStatus = isFreightLocated
    ? FREIGHT_LOCATED_STEP_MAP[
        lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
      ]
    : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];

  const shouldShowCompleteButton =
    formFieldsDisabled &&
    hasCompleteShortagesPermission &&
    status === EShortageInvestigationStatus.pendingSrApproval &&
    adjustedStatus === activeStep;

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isShowSendAPB, setIsShowSendAPB] = useState<boolean>(false);

  // If active day investigation is completed the checkboxes is disabled of day
  const isControlDisabled = useCallback((fieldName: keyof IAllShortageInvestigation) => {
    const value = "Y";
    const isChecked =
      typeof initValues[fieldName] === "string" &&
      initValues[fieldName] === value;
    if (
      (status > EShortageInvestigationStatus.day20InProgress ||
        isFreightLocated) &&
      isChecked
    ) {
      return true;
    }
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    if (isChecked) {
      return true;
    }
  }, [status, isFreightLocated, activeStep, initValues]);

  const onChangeRadio = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    handleChange(event);
    if (value === "Y") {
      setFieldValue("drCheckSteps", initValues["drCheckSteps"], true);
    } else {
      setFieldValue("drCheckSteps", "", true);
      setInvalidFields(invalidFields.filter((field) => field !== "drCheckSteps"));
    }
  };

  // Enable fields in edit mode upon clicking the edit button
  const enableEditing = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevents the default form submission behavior
    setFormFieldsDisabled(false); // Enables the form fields for editing
  }, []);

  // Initialize form handling using Formik
  // This setup uses Formik to manage the form state, validation, and submission process.
  const form = useFormik({
    validationSchema: ASInvestigationSchema[activeStep], // Validation schema for the form fields
    validateOnChange: false, // Disable validation on field change
    initialValues: initValues, // Initial values for the form fields
    initialTouched: {
      showAtDestTerm: true,
      noMovement: true,
      pumCheck: true,
      check7818: true,
      contactTerminals: true,
      followUp: true,
      check7818_2: true,
      finalReCheck: true,
    },
    onSubmit: () => {
      saveOrSubmit(EFormAction.submit); // Function to handle form submission
    },
  });

  // Destructure various handlers and values provided by Formik for easy use within the component
  const {
    values, // Current values of the form fields
    handleChange, // Handler for changing form field values
    handleBlur, // Handler for managing field blur events
    validateField, // Function to validate a specific field
    setFieldValue, // Function to set the value of a specific field
    validateForm, // Function to validate the entire form
    resetForm,
    errors, // Function to reset the form to its initial values
  } = form;

  const callSameDaysChunks = chunkArray(values?.callSameDaysDataset, 8);

  const uploadInvestigation = useCallback(async () => {
    setPageLoading(true);
    let ids = AS_INVESTIGATION_WIZARD_STEPS;
    if (isFreightLocated) {
      ids = ids.slice(
        0,
        INVESTIGATION_DONE_STEP_MAP[
          lastStatusNumber as keyof typeof INVESTIGATION_DONE_STEP_MAP
        ]
      );
    }
    const imagePromises: Promise<File>[] = ids.flatMap(
      (day: string, index: number) => {
        const promises: Promise<File>[] = [];

        if (day === "Day 4") {
          const defaultId = `investigation-${day}`;
          promises.push(
            htmlToImage(defaultId, `${index + 1}.jpeg`),
            ...callSameDaysChunks.map((_c, chunkIndex) => {
              const chunkId = `investigation-${day}-callSameDays-${chunkIndex}`;
              return htmlToImage(
                chunkId,
                `${day}-page-${chunkIndex + 1}.jpeg`,
                MAX_FILE_SIZE
              );
            })
          );
        } else {
          const defaultId = `investigation-${day}`;
          promises.push(
            htmlToImage(
              defaultId,
              `${index + 1}.jpeg`,
              day === "Day 3" ? MAX_FILE_SIZE : undefined
            )
          );
        }

        return promises;
      }
    );
    const images = await Promise.all(imagePromises);
    const reqInput: IUploadFile = {
      osdNumber: investigationDetails?.osdNumber?.toString() ?? "",
      docType: "FX",
      uploadKey: EUploadKey.pro,
    };

    images.forEach((image, index) => {
       console.log("AS Generated image:", index, URL.createObjectURL(image));
    });

    const headers = { "OSD-Module-Name": "shortages", "OSD-Section-Name": "investigation", "OSD-Action-Type": "upload", "OSD-Exception-Type": "AS", "OSD-Reporting-Terminal": investigationDetails?.reporting ?? "", "OSD-Destination-Terminal": investigationDetails?.destination ?? "" };

    
    FileUploadAPI(images, reqInput, headers)
      .then((res: any) => {
        setPageLoading(false);
        const errors = get(res, "errors", []);
        const notificationType =
          errors.length > 0
            ? NOTIFICATION_TYPE.ERROR
            : NOTIFICATION_TYPE.SUCCESS;
        const message =
          errors.length > 0
            ? errors.join(", ")
            : res.result ?? "OS&D investigation image uploaded successfully";

        showNotification(notificationType, message);
      })
      .catch((error: any) => {
        setPageLoading(false);
        showNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : `OS&D investigation image uploading failed`
        );
      });
  }, [
    callSameDaysChunks,
    isFreightLocated,
    lastStatusNumber,
    investigationDetails?.osdNumber,
    showNotification,
    investigationDetails?.destination,
    investigationDetails?.reporting
  ]);

  const getPageIndex = (index: number, callSameDaysChunks: any[]) => {
    if (index === 1) {
      return callSameDaysChunks[0].length;
    }
    if (index === 2) {
      return [...callSameDaysChunks[0], ...callSameDaysChunks[1]].length;
    }
    return 0;
  };
  const isValidDependedField = (
    fieldName: keyof IAllShortageInvestigation,
    dependedField: keyof IAllShortageInvestigation,
    activeStep: number,
    val: string = "Y"
  ) => {
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    if (
      values[dependedField] === val &&
      values[fieldName] &&
      typeof values[fieldName] === "string" &&
      values[fieldName] === "" &&
      !invalidFields.includes(fieldName)
    ) {
      setInvalidFields([...invalidFields, fieldName]);
    } else {
      setInvalidFields(invalidFields.filter((field) => field !== fieldName));
    }
  };

  const addRemove = (type: string = "add") => {
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    const l = values?.freightTerminalLeadInquiries?.length!;

    if (type === "add") {
      setInvalidFields([
        ...invalidFields,
        `manifest${l}`,
        `terminal${l}`,
        `discrepancies${l}`,
      ]);
    } else {
      setInvalidFields(
        invalidFields.filter(
          (field) =>
            ![
              `manifest${l - 1}`,
              `terminal${l - 1}`,
              `discrepancies${l - 1}`,
            ].includes(field)
        )
      );
    }
  };

  // check text field box value exist and on blank show error for completed investigation day
  const isValueExist = useCallback((
    fieldName: string,
    index?: number,
    fieldArrayName: string = "freightTerminalLeadInquiries"
  ) => {
    // Exit if the current step is not active
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    const hasIndex = index !== undefined;

    const fieldKey = hasIndex ? `${fieldName}${index}` : fieldName;
    const value =
      hasIndex
        ? get(values, `${fieldArrayName}[${index}][${fieldName}]`, "")
        : values[fieldName as keyof IAllShortageInvestigation] ?? "";

    const initValue =
      hasIndex
        ? get(initValues, `${fieldArrayName}[${index}][${fieldName}]`, "")
        : initValues[fieldName as keyof IAllShortageInvestigation];
    const isInvalid =
      (value == null || value === "") && // Covers null, undefined, and empty string
      ((index === undefined && initValue !== "" && initValue != null) ||
        hasIndex) && // Checks if initValue is neither an empty string, null, nor undefined
      !invalidFields.includes(fieldKey);

    // Update invalid fields
    setInvalidFields((prevInvalidFields) =>
      isInvalid
        ? [...prevInvalidFields, fieldKey]
        : prevInvalidFields.filter((field) => field !== fieldKey)
    );
  }, [status, activeStep, values, initValues, invalidFields]);

  const proInfoAddRemove = (type: "add" | "remove" = "add") => {
     if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    // Determine the length based on the type
    const length =
      (values?.callSameDaysDataset?.length ?? 0) - (type === "add" ? 0 : 1);

    const fields = [`sameDayPro${length}`, `deliveryInfo${length}`];

    setInvalidFields((prevFields) =>
      type === "add"
        ? [...prevFields, ...fields]
        : prevFields.filter((field) => !fields.includes(field))
    );
  };

  const reset = useCallback(() => {
    resetForm({values: initValues});
    setIsCompleting(false);
    setResetTimestamps(Date.now().toString());
    setInvalidFields([]);
  }, [initValues, resetForm]);

  // validate day 5 investigation Details for add and remove buttons
  const validateDay5Fields = () => {
      values?.freightTerminalLeadInquiries?.forEach((_: any, index: number) => {
        validateField(`freightTerminalLeadInquiries[${index}].terminal`);
        validateField(`freightTerminalLeadInquiries[${index}].manifest`);
        validateField(`freightTerminalLeadInquiries[${index}].discrepancies`);
      });
  };

  // Updated Initial values on page load and save, submit, and complete action
  const updateInitValue = (
    key: string,
    value: string | number | null | undefined
  ) => {
    setInitValues((prevState) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  // handle confirmation pop while user want back according button
  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);
  
  // Investigation complete and upload images
  const handleFreightAndCompletion = useCallback(async (action: EFormAction) => {
    try {
      setPageLoading(true);
      const submitResponse = await ShortageInvestigationCreateUpdateAPI(
        {},
        investigationDetails?.osdNumber?.toString(),
        action,
        action === EFormAction.freightLocated ? activeDay.toString() : undefined,
      );
      const {
        status: statusNumber,
        investigationDoneTillTheDay: lastStatusNumber,
        freightLocated,
      } = submitResponse;
      setStatus(statusNumber);
      setLastStatusNumber(lastStatusNumber);
      setIsFreightLocated(freightLocated);
      setInvestigationStatus(statusNumber);
      setPageLoading(false);
      setFormKey(Date.now().toString());
      let message;
      if (action === EFormAction.complete) {
        message = ErrorMessages.investigationNoteCompleted;
        uploadInvestigation();
      } else {
        setActiveStep(
          FREIGHT_LOCATED_STEP_MAP[
            lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
          ]
        );
        message = ErrorMessages.freightLocatedSuccess;
      }
      showNotification(NOTIFICATION_TYPE.SUCCESS, message);
    } catch (error) {
      setPageLoading(false);
      showNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof error === "string"
          ? error
          : `OS&D investigation notes submit failed`
      );
    }
  }, [investigationDetails?.osdNumber, activeDay, uploadInvestigation, setInvestigationStatus, showNotification]);
  // Close the confirmation modal pop and navigate user to back All Shortage listing page if user confirmed
  const handleDialogClose = useCallback((flag?: string) => {
    setDialogOpen(false);
    if (flag === "yes") {
      handleFreightAndCompletion(EFormAction.freightLocated);
    }
  }, [handleFreightAndCompletion]);

  const handleDateChange =
    (fieldName: string) => (value: dayjs.Dayjs | null) => {
      if (value?.format("MM/DD/YY") === "Invalid Date") {
        setDateKeys((prevKeys) => ({
          ...prevKeys,
          [fieldName]: Date.now().toString(),
        }));
      } else {
        setFieldValue(fieldName, value?.format("MM/DD/YY"), true);
        setOpen(false);
      }
    };

  const hasError = useCallback((fieldName: keyof IAllShortageInvestigation) => {
    return Boolean(errors[fieldName]) && isCompleting;
  }, [errors, isCompleting]);

  const hasFreightTerminalError = (index: number, keyField: string) => {
    return (
      isCompleting &&
      (invalidFields.includes(keyField + index) ||
        Boolean(
          get(errors, `freightTerminalLeadInquiries[${index}][${keyField}]`)
        ))
    );
  };

  const saveCompleteBtnClassName = () => {
    if (invalidFields?.length > 0) return "disable-btn disable entry ml";
    return "entry ml";
  };

  const saveOrSubmit = async (action: EFormAction) => {
    if (invalidFields?.length > 0) return;
    setIsCompleting(false);

    if (await shouldAbortDueToErrors(action)) return;
    if (shouldAbortDueToInvalidFields()) return;
    if (focusOnRequiredField()) return;

    let reqParams = createRequestParams();
    reqParams = omitBy(reqParams, isUndefined);

    if (isEmpty(reqParams)) return;

    // Trim all of string variables
    reqParams = trimStrings(reqParams);
    setPageLoading(true);

    submitRequest(reqParams, action);
  };

  const shouldAbortDueToErrors = async (action: EFormAction) => {
    // Ensure the form is fully validated before proceeding
    const errorList = await form.validateForm();
    const requiredSteps =
      STEPS_REQUIRED_MAP[lastStatusNumber as keyof typeof STEPS_REQUIRED_MAP] ||
      [];

    // Check if the action is either 'submit' or 'save' (validate the save form if the Investigation Day is already completed) with valid conditions

    const isValidAction =
      action === EFormAction.submit ||
      (action === EFormAction.save && requiredSteps.includes(activeStep));

    // If the action is valid and there are errors, abort submission
    if (isValidAction && Object.keys(errorList).length > 0) {
      setIsCompleting(true);
      // Focus on the first error field if there are validation errors
      const firstErrorField = Object.keys(errors)[0];
      document.getElementsByName(firstErrorField)[0]?.focus();
      return true; // Abort due to validation errors
    }

    // If there are no errors, proceed with submission
    return false;
  };

  const shouldAbortDueToInvalidFields = () => {
    return invalidFields?.length > 0;
  };

  const focusOnRequiredField = () => {
    if (activeStep === STEP_STATUS_MAP[6]) {
      const isAllSameDayProValid = values?.callSameDaysDataset?.every(
        (item, index) => {
          if (Number.isNaN(+(item?.sameDayPro ?? ""))) {
            // Focus on the invalid field
            const field = document.getElementsByName(
              `callSameDaysDataset[${index}].sameDayPro`
            )[0];
            field?.focus();
            return false;
          }
          return true;
        }
      );

      if (!isAllSameDayProValid) {
        return true; // Prevent save
      }
    }
    if (activeStep === STEP_STATUS_MAP[4] && values.pickupManifest === 0) {
      document.getElementsByName("pickupManifest")[0].focus();
      return true;
    }

    if (activeStep === STEP_STATUS_MAP[6]) {
      return focusOnZeroManifestField();
    }

    return false;
  };

  const focusOnZeroManifestField = () => {
    let isManifestZero = false;
    (values.freightTerminalLeadInquiries ?? []).forEach(
      (obj: IDay5Investigation, index: number) => {
        if (obj.manifest === 0) {
          document
            .getElementsByName(
              `freightTerminalLeadInquiries[${index}].manifest`
            )[0]
            .focus();
          isManifestZero = true;
        }
      }
    );
    return isManifestZero;
  };

  const createRequestParams = () => {
    let reqParams: any = structuredClone(values);
    const deleteFields: string[] = [];
    const allFields = AS_ALL_INVESTIGATION_FIELDS_GROUPED;
    if (activeStep >= 0 && activeStep < allFields.length) {
      reqParams = Object.fromEntries(
        Object.entries(reqParams).filter(([key]) =>
          allFields[activeStep].includes(key)
        )
      );
    }

    handleConditionalFields(reqParams, deleteFields);
    deleteFields.forEach((field) => delete reqParams[field]);

    formatRequestParams(reqParams);

    const { freightTerminalLeadInquiries, callSameDaysDataset, ...data } =
      reqParams;

    reqParams = {
      data,
      ...(freightTerminalLeadInquiries ? { freightTerminalLeadInquiries } : {}),
      ...(callSameDaysDataset ? { callSameDaysDataset } : {}),
      ...(isShowSendAPB ? { attachmentImageUrls: selectedImages } : {}),
    };

    return reqParams;
  };

  const handleConditionalFields = (reqParams: any, deleteFields: string[]) => {
    if (reqParams["contactTerminals"]) {
      if (reqParams["contactTerminals"] === "N") {
        deleteFields.push("freightTerminalLeadInquiries");
      }
    } else {
      deleteFields.push("contactTerminals", "freightTerminalLeadInquiries");
    }

    if (reqParams["callSameDays"]) {
      if (reqParams["callSameDays"] === "N") {
        deleteFields.push("callSameDaysDataset");
      }
    } else {
      deleteFields.push("callSameDays", "callSameDaysDataset");
    }
  };

  const formatRequestParams = (reqParams: any) => {
    // Convert specific fields to the correct types

    if (reqParams.callSameDaysDataset) {
      reqParams.callSameDaysDataset = reqParams.callSameDaysDataset.map(
        (obj: ICallSameDaysDataset) => ({
          sameDayPro: obj.sameDayPro === 0 ? "" : obj.sameDayPro?.toString(),
          deliveryInfo: obj.deliveryInfo === null ? "" : obj.deliveryInfo,
        })
      );
    } else {
      delete reqParams.callSameDaysDataset;
    }

    // Convert `pickupManifest` to an integer if it exists and is a valid number
    if (reqParams.pickupManifest && !Number.isNaN(reqParams.pickupManifest)) {
      reqParams.pickupManifest = Number.parseInt(reqParams.pickupManifest);
    } else {
      delete reqParams.pickupManifest;
    }

    // Process `freightTerminalLeadInquiries` if present
    if (Array.isArray(reqParams.freightTerminalLeadInquiries)) {
      reqParams.freightTerminalLeadInquiries =
        reqParams.freightTerminalLeadInquiries.map(
          (item: IDay5Investigation) => ({
            ...item,
            manifest: item.manifest ? +item.manifest : undefined,
          })
        );
    }
  };

  const submitRequest = (reqParams: any, action: EFormAction) => {
    ShortageInvestigationCreateUpdateAPI(
      reqParams,
      investigationDetails?.osdNumber?.toString(),
      action,
      activeDay.toString()
    )
      .then((res) => handleSuccessfulSubmission(res, action))
      .catch((error) => handleSubmissionError(error, action));
  };

  const handleSuccessfulSubmission = (res: any, action: EFormAction) => {
    if (action === EFormAction.save) setFormFieldsDisabled(true);
    setIsCompleting(false);

    processRequestFields();
    displaySuccessMessage(action);

    handleStatusAndUIUpdates(res);

    setPageLoading(false);
    validateForm(values);
    dispatch(isFormValueChanged(false));
  };

  const handleSubmissionError = useCallback((err: any, action: EFormAction) => {
    setPageLoading(false);
    showNotification(
      NOTIFICATION_TYPE.ERROR,
      typeof err === "string"
        ? err
        : `OS&D investigation notes ${action} failed`
    );
  }, [showNotification]);

  const checkValueNull = (value: string | number | null | undefined) => {
    return value === null ? "" : value;
  };
  const processFormValues = () => {
    const reqParams = trimStrings(values);
    if (reqParams.freightTerminalLeadInquiries) {
      reqParams.freightTerminalLeadInquiries = (
        reqParams.freightTerminalLeadInquiries ?? []
      ).map((obj: IDay5Investigation) => ({
        manifest: obj.manifest === 0 ? undefined : obj.manifest,
        discrepancies: checkValueNull(obj.discrepancies),
        terminal: checkValueNull(obj.terminal),
      }));
    } else {
      delete reqParams.freightTerminalLeadInquiries;
    }

    reqParams.callSameDaysDataset = (reqParams.callSameDaysDataset ?? []).map(
      (obj: ICallSameDaysDataset) => ({
        sameDayPro:
          obj.sameDayPro === null || obj.sameDayPro === "0"
            ? ""
            : obj.sameDayPro,
        deliveryInfo: checkValueNull(obj.deliveryInfo),
      })
    );
    return reqParams;
  };

  const processRequestFields = () => {
    const reqParams = processFormValues();

    Object.keys(reqParams).forEach((field: string) => {
      if (allFields.includes(field)) {
        const value =
          field === "pickupManifest" && reqParams[field] === 0
            ? undefined
            : reqParams[field];
        setFieldValue(field, value);
        updateInitValue(field, value);
      }
    });
  };

  const displaySuccessMessage = useCallback((action: EFormAction) => {
    const message =
      action === EFormAction.save
        ? ErrorMessages.investigationNoteSaved
        : `OS&D investigation day ${activeDay} notes have been completed successfully`;

    showNotification(NOTIFICATION_TYPE.SUCCESS, message);
  }, [activeDay, showNotification]);

  const validateCallSameDaysDataset = useCallback(async () => {
    if (!values.callSameDaysDataset?.length) return;
    for (let index = 0; index < values.callSameDaysDataset.length; index++) {
      const validateFields = ["sameDayPro", "deliveryInfo"];
      for (const field of validateFields) {
        await validateField(`callSameDaysDataset[${index}].${field}`);
      }
    }
  }, [values.callSameDaysDataset, validateField]);

  const handleStatusAndUIUpdates = (res: any) => {
    const statusNumber = res.status;
    if (
      status === statusNumber ||
      statusNumber > EShortageInvestigationStatus.day20InProgress ||
      (status === EShortageInvestigationStatus.exceptionEntry &&
        statusNumber === EShortageInvestigationStatus.day1InProgress)
    ) {
      setFormFieldsDisabled(true);
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
    setStatus(statusNumber);
    setIsFreightLocated(res.freightLocated);
    setInvestigationStatus(statusNumber);
    setLastStatusNumber(res.investigationDoneTillTheDay);

    if (res.freightLocated) {
      setLastStatusNumber(res.investigationDoneTillTheDay);
      setActiveStep(
        FREIGHT_LOCATED_STEP_MAP[
          res.investigationDoneTillTheDay as keyof typeof FREIGHT_LOCATED_STEP_MAP
        ]
      );
    } else {
      setActiveStep(
        STEP_STATUS_MAP[statusNumber as keyof typeof STEP_STATUS_MAP]
      );
    }
  };

  const getCallSameDaysDataset = (checked: boolean) => {
    if (checked) {
      return Array.isArray(values.callSameDaysDataset) &&
        values.callSameDaysDataset.length > 0
        ? values.callSameDaysDataset
        : ASInvestigationInitValue.callSameDaysDataset;
    }
    return [];
  };

  const getFreightTerminalLeadInquiries = (checked: boolean) => {
    if (checked) {
      return Array.isArray(values.freightTerminalLeadInquiries) &&
        values.freightTerminalLeadInquiries.length > 0
        ? values.freightTerminalLeadInquiries
        : ASInvestigationInitValue.freightTerminalLeadInquiries;
    }
    return [];
  };

  const getErrorMessage = useCallback((field: keyof IAllShortageInvestigation) => {
    if (errors?.[field] && isCompleting) {
      return <span className="form-error">{errors?.[field]}</span>;
    }
    return null;
  }, [errors, isCompleting]);

  const renderCompletedError = useCallback((
    fieldName: string,
    day: keyof typeof EASInvestigationFieldsErrorMessage,
    conditionalFlag: boolean = true
  ) => {
    if (invalidFields.includes(fieldName) && conditionalFlag) {
      return (
        <span className="form-error">
          {EASInvestigationFieldsErrorMessage[day]}
        </span>
      );
    }
    return null;
  }, [invalidFields]);

  const renderFormArrayError = (
    index: number,
    fieldKey: string,
    formArrayName: string = "freightTerminalLeadInquiries"
  ) => {
    if (
      !invalidFields.includes(fieldKey + index) &&
      get(errors, `${formArrayName}[${index}][${fieldKey}]`) &&
      isCompleting
    ) {
      return (
        <span className="form-error">
          {get(errors, `${formArrayName}[${index}][${fieldKey}]`, "")}
        </span>
      );
    }
    return null;
  };

  const getDate = (value: string | undefined) => {
    return dayjs(value ?? null);
  };

  const isFieldDisabled = useCallback((fieldName: keyof IAllShortageInvestigation) => {
    return formFieldsDisabled || isControlDisabled(fieldName);
  }, [formFieldsDisabled, isControlDisabled]);

  // Reusable component for checkbox questions with conditional text field
  const renderCheckboxQuestion = useCallback(
    (
      fieldName: keyof IAllShortageInvestigation,
      textFieldName: keyof IAllShortageInvestigation,
      label: string | JSX.Element,
      tooltip?: { text: string; key: string },
      additionalContent?: JSX.Element,
      onCheckboxChange?: (checked: boolean) => void
    ) => {
      // Determine the day based on active step
      const stepToDayMap: Record<number, keyof typeof EASInvestigationFieldsErrorMessage> = {
        [STEP_STATUS_MAP[3]]: "day2",
        [STEP_STATUS_MAP[4]]: "day3",
        [STEP_STATUS_MAP[5]]: "day4",
      };
      const dayLabel: keyof typeof EASInvestigationFieldsErrorMessage = 
        stepToDayMap[activeStep] ?? "day5";

      return (
        <FormControl className="radio-group question-group ">
          <FormLabel>
            <FormControlLabel
              className="checkbox-shoartage"
              disabled={isFieldDisabled(fieldName)}
              control={
                <Checkbox
                  disableRipple
                  checked={values[fieldName] === "Y"}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    setFieldValue(fieldName, checked ? "Y" : "N");
                    setFieldValue(
                      textFieldName,
                      checked ? values[textFieldName] : "",
                      true
                    );
                    onCheckboxChange?.(checked);
                  }}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
              label=""
            />
            {label} <sup>*</sup>{" "}
            {tooltip && (
              <>
                &nbsp;
                <TooltipWithClickAway
                  tooltipText={tooltip.text}
                  tooltipKey={tooltip.key}
                />
              </>
            )}
          </FormLabel>
          {getErrorMessage(fieldName)}
          <Grid container spacing={2}>
            <Grid item {...XL_GRID_PROPS} className="all-short-entry">
              {values[fieldName] === "Y" && (
                <>
                  <TextField
                    autoComplete="off"
                    name={textFieldName}
                    value={values[textFieldName]}
                    error={hasError(textFieldName)}
                    fullWidth
                    placeholder="Describe Steps Taken"
                    inputProps={{
                      maxLength: MAX_LENGTH_SUMMARY_TEXT,
                    }}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      isValueExist(textFieldName);
                    }}
                    disabled={formFieldsDisabled}
                    variant="outlined"
                  />
                  {additionalContent}
                </>
              )}
              {renderCompletedError(textFieldName, dayLabel)}
              {getErrorMessage(textFieldName)}
            </Grid>
          </Grid>
        </FormControl>
      );
    },
    [
      values,
      formFieldsDisabled,
      handleChange,
      handleBlur,
      setFieldValue,
      activeStep,
      isFieldDisabled,
      getErrorMessage,
      hasError,
      isValueExist,
      renderCompletedError,
    ]
  );

  // Reusable component for simple text fields with label
  const renderSimpleTextField = useCallback(
    (
      fieldName: keyof IAllShortageInvestigation,
      label: string,
      placeholder: string,
      maxLength: number = 30,
      mdSize: number = 3,
      day: keyof typeof EASInvestigationFieldsErrorMessage = "day3"
    ) => {
      return (
        <Grid item md={mdSize} xs={12} className="pt-0">
          <FormControl className="radio-group question-group ">
            <FormLabel className="mb-10">
              {label} <sup>*</sup>
            </FormLabel>
            <TextField
              autoComplete="off"
              name={fieldName}
              value={values[fieldName]}
              error={hasError(fieldName)}
              fullWidth
              inputProps={{ maxLength }}
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
                isValueExist(fieldName);
              }}
              disabled={formFieldsDisabled}
              variant="outlined"
              placeholder={placeholder}
            />
          </FormControl>
          {getErrorMessage(fieldName)}
          {renderCompletedError(fieldName, day)}
        </Grid>
      );
    },
    [values, formFieldsDisabled, handleChange, handleBlur, getErrorMessage, hasError, isValueExist, renderCompletedError]
  );

  const renderF18Note = (fieldName: keyof IAllShortageInvestigation) => {
    const dayF18Notes: any = {
      day1F18Note: "day1",
      day2F18Note: "day2",
      day3F18Note: "day3",
      day4F18Note: "day4",
      day5F18Note: "day5",
      day10F18Note: "day10",
      day15F18Note: "day15",
      day20F18Note: "day20",
    };
    const day: string = dayF18Notes[fieldName];
    return (
      <Grid item {...XL_GRID_PROPS}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
            F18 Notes <sup>*</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            multiline
            rows={2}
            name={fieldName}
            key={fieldName}
            id={fieldName}
            value={values[fieldName]}
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);
              isValueExist(fieldName);
            }}
            placeholder="Enter F18 Notes"
            fullWidth
            inputProps={{ maxLength: 300 }}
            disabled={formFieldsDisabled}
            variant="outlined"
          />
        </FormControl>
        {getErrorMessage(fieldName)}
        {renderCompletedError(
          fieldName,
          day as keyof typeof EASInvestigationFieldsErrorMessage
        )}
      </Grid>
    );
  };

  const renderDatePicker = (
    fieldKey: "dateToDestTerm" | "dateLastMovement"
  ) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          disableFuture
          open={open}
          key={dateKeys[fieldKey]}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={getDate(values[fieldKey])}
          name={fieldKey}
          onChange={handleDateChange(fieldKey)}
          format="MM/DD/YYYY"
          slotProps={{
            textField: {
              error: hasError(fieldKey),
              onBlur: (e) => {
                handleBlur(e);
                if (fieldKey === "dateToDestTerm") {
                  isValidDependedField(
                    "dateToDestTerm",
                    "showAtDestTerm",
                    STEP_STATUS_MAP[2],
                    "N"
                  );
                }
              },
              onClick: () => setOpen(true),
              inputProps: {
                readOnly: true,
              },
            },
          }}
          disabled={formFieldsDisabled}
          className="col-md-4 mt-1"
        />
      </LocalizationProvider>
    );
  };

  // Assuming `displayCompleteBtn`, `isFreightLocated`, `lastStatusNumber`, `status`, `activeStep`, and `STEP_STATUS_MAP` are all defined

  // Use `useEffect` to set `displayCompleteBtn` when dependencies change
  useEffect(() => {
    const adjustedStatus = isFreightLocated
      ? FREIGHT_LOCATED_STEP_MAP[
          lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
        ]
      : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];
    // Determine the condition to display the complete button
    const shouldDisplayCompleteBtn = !(
      isFreightLocated ||
      adjustedStatus > activeStep ||
      status === EShortageInvestigationStatus.pendingSrApproval ||
      (!isFreightLocated && status === EShortageInvestigationStatus.complete)
    );

    setDisplayCompleteBtn(shouldDisplayCompleteBtn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreightLocated, lastStatusNumber, status, activeStep, STEP_STATUS_MAP]);

  useEffect(() => {
    validateForm().then((formErrors: FormikErrors<any>) => {
      const freightTerminalLeadInquiriesErrors =
        formErrors.freightTerminalLeadInquiries;
      // Check if any manifest in the freightTerminalLeadInquiries array is zero
      const hasZeroManifest = values.freightTerminalLeadInquiries?.some(
        (detail) => detail.manifest === 0
      );
      if (freightTerminalLeadInquiriesErrors?.length) {
        validateDay5Fields();
        setAddButtonDisabled(true);
      } else if (hasZeroManifest) {
        setAddButtonDisabled(true);
      } else {
        setAddButtonDisabled(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleStatus = (investigationDetails: any) => {
    const { investigationFreightLocated, investigationDoneTillTheDay } =
      investigationDetails;
    setIsFreightLocated(investigationFreightLocated);
    setLastStatusNumber(investigationDoneTillTheDay ?? 1);

    if (investigationDetails.investigationStatus) {
      const stepMap =
        isFreightLocated || investigationDetails?.investigationFreightLocated
          ? FREIGHT_LOCATED_STEP_MAP
          : STEP_STATUS_MAP;

      const stepKey =
        isFreightLocated || investigationDetails?.investigationFreightLocated
          ? investigationDetails?.investigationDoneTillTheDay
          : investigationDetails.investigationStatus;

      setActiveStep(stepMap[stepKey as keyof typeof stepMap]);
      setStatus(investigationDetails.investigationStatus);
    }
  };

  useEffect(() => {
    if (!investigationDetails) return;
    handleStatus(investigationDetails);

    // Normalize investigation details, setting manifest to undefined if it's 0
    const normalizedDetails =
      investigationDetails?.freightTerminalLeadInquiries?.map(
        ({
          terminal,
          manifest,
          discrepancies,
        }: {
          terminal: string;
          manifest: number | undefined;
          discrepancies: string;
        }) => ({
          terminal,
          manifest: manifest === 0 ? undefined : manifest,
          discrepancies,
        })
      ) ?? [];

    // Update investigation details
    investigationDetails.freightTerminalLeadInquiries = normalizedDetails;

    // Prepare the list of all fields to iterate
    if (
      investigationDetails.contactTerminals === "Y" &&
      normalizedDetails.length === 0
    ) {
      investigationDetails.freightTerminalLeadInquiries = [
        { terminal: undefined, manifest: undefined, discrepancies: undefined },
      ];
    }

    if (
      (!investigationDetails?.callSameDaysDataset ||
        investigationDetails?.callSameDaysDataset?.length < 1) &&
      investigationDetails?.callSameDays === "Y"
    ) {
      investigationDetails.callSameDaysDataset = [
        { sameDayPro: undefined, deliveryInfo: undefined },
      ];
    }

    allFields.forEach((key) => {
      const value =
        investigationDetails[key] === 0 ? undefined : investigationDetails[key];
      setFieldValue(key, value);
      updateInitValue(key, value);
    });

    normalizedDetails.forEach((obj: IDay5Investigation, index: number) => {
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].terminal`,
        obj.terminal
      );
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].manifest`,
        obj.manifest
      );
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].discrepancies`,
        obj.discrepancies
      );
    });

    investigationDetails?.callSameDaysDataset?.forEach(
      (obj: ICallSameDaysDataset, index: number) => {
        setFieldValue(
          `callSameDaysDataset[${index}].sameDayPro`,
          obj.sameDayPro
        );
        setFieldValue(
          `callSameDaysDataset[${index}].deliveryInfo`,
          obj.deliveryInfo
        );
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  useEffect(() => {
    if (!values) return; // Skip validation if no values

    const debouncedValidation = debounce(async () => {
      try {
        const formErrors = await validateForm();
        const proDeliveryInfoErrors = formErrors.callSameDaysDataset;

        if (proDeliveryInfoErrors?.length) {
          validateCallSameDaysDataset();
          setProInfoAddButtonDisabled(true);
        } else {
          setProInfoAddButtonDisabled(false);
        }
      } catch (error) {
        console.error("Error during validation:", error);
        setProInfoAddButtonDisabled(true);
      }
    }, 300);

    debouncedValidation();

    return () => debouncedValidation.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, validateForm, validateCallSameDaysDataset]);

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);
  return (
    <div>
      <AccordionDetails className="accordion-container">
        <InvestigationStepper
          activeStep={activeStep}
          steps={AS_INVESTIGATION_WIZARD_STEPS}
          isFreightLocated={isFreightLocated}
          lastStatusNumber={lastStatusNumber}
          status={status}
          setIsCompleting={setIsCompleting}
          setFormFieldsDisabled={setFormFieldsDisabled}
          setIsEditMode={setIsEditMode}
          reset={reset}
          setActiveStep={setActiveStep}
        />
        <FormikProvider key={resetTimestamps} value={form}>
          <>
            {activeStep === STEP_STATUS_MAP[1] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="destinationTerminal">
                        Does the freight show at the destination terminal?{" "}
                        <sup>*</sup> &nbsp;
                        <TooltipWithClickAway
                          tooltipText="Freight must be forced to the DT if not already showing there"
                          tooltipKey="tooltip1"
                        />
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="destinationTerminal"
                            name="destinationTerminal"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="showAtDestTerm"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue("dateToDestTerm", "", true);
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.showAtDestTerm === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="showAtDestTerm"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue(
                                  "dateToDestTerm",
                                  initValues["dateToDestTerm"],
                                  true
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.showAtDestTerm === "N"}
                            />
                          </RadioGroup>

                          {values.showAtDestTerm === "N" && (
                            <>
                              <FormLabel id="dateToDestTerm">
                                Date forced to destination terminal <sup>*</sup>
                              </FormLabel>
                              {renderDatePicker("dateToDestTerm")}
                            </>
                          )}
                        </Grid>
                      </Grid>
                      {renderCompletedError("dateToDestTerm", "day1")}
                      {getErrorMessage("showAtDestTerm")}
                      {getErrorMessage("dateToDestTerm")}
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.notInDestTerm === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "notInDestTerm",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "notInDestTermSteps",
                                  e.target.checked
                                    ? values.notInDestTermSteps
                                    : "",
                                  true
                                );
                              }}
                              disabled={isFieldDisabled("notInDestTerm")}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />{" "}
                        Confirm freight is not in the destination terminals OS&D
                        bay or on their Overage report <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("notInDestTerm")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.notInDestTerm === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="notInDestTermSteps"
                              value={values.notInDestTermSteps}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                  {/* F18 Notes */}
                  {renderF18Note("day1F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[3] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.noMovement === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "noMovement",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "dateLastMovement",
                                  e.target.checked
                                    ? values.dateLastMovement
                                    : "",
                                  true
                                );
                                setFieldValue(
                                  "noMovementSteps",
                                  e.target.checked
                                    ? values.noMovementSteps
                                    : "",
                                  true
                                );
                              }}
                              disabled={isFieldDisabled("noMovement")}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Confirm the pro shows no movement ? <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("noMovement")}
                      <Grid container spacing={2}>
                        <Grid item md={4} xs={12} className="all-short-entry">
                          {values.noMovement === "Y" && (
                            <FormLabel
                              id="dateLastMovement"
                              className="date-label-margin mt-0"
                            >
                              Select date of last movement <sup>*</sup>
                            </FormLabel>
                          )}
                          {values.noMovement === "Y" && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                disableFuture
                                value={dayjs(values.dateLastMovement ?? null)}
                                name="dateLastMovement"
                                open={open}
                                key={dateKeys.dateLastMovement}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                onChange={handleDateChange("dateLastMovement")}
                                format="MM/DD/YYYY"
                                slotProps={{
                                  textField: {
                                    error:
                                      !!errors.dateLastMovement && isCompleting,
                                    onBlur: handleBlur,
                                    onClick: () => {
                                      setOpen(true);
                                    },
                                    inputProps: {
                                      readOnly: true,
                                    },
                                  },
                                }}
                                disabled={formFieldsDisabled}
                                className="col-md-12"
                              />
                            </LocalizationProvider>
                          )}
                          {errors.dateLastMovement && isCompleting ? (
                            <span className="form-error">
                              {errors.dateLastMovement}
                            </span>
                          ) : null}
                        </Grid>
                        <Grid item md={8} xs={12} className="all-short-entry">
                          {values.noMovement === "Y" && (
                            <FormLabel
                              id="noMovementSteps"
                              className="date-label-margin mt-2"
                            >
                              &nbsp;
                            </FormLabel>
                          )}
                          {values.noMovement === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="noMovementSteps"
                              value={values.noMovementSteps}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                          )}
                          {invalidFields.includes("noMovementSteps") && (
                            <span className="form-error">
                              {EASInvestigationFieldsErrorMessage.day2}
                            </span>
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.suffixBill === "Y"}
                              disabled={isFieldDisabled("suffixBill")}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "suffixBill",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "suffixBillSteps",
                                  e.target.checked
                                    ? values.suffixBillSteps
                                    : "",
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />{" "}
                        Check if a suffix bill or OD400 was cut to move some or
                        all of the freight <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="If yes, view the AS exception & confirm it was cleared properly. If no, move to next step."
                          tooltipKey="tooltip2"
                        />
                      </FormLabel>
                      {getErrorMessage("suffixBill")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.suffixBill === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="suffixBillSteps"
                              value={values.suffixBillSteps}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              error={hasError("suffixBillSteps")}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                                isValueExist("suffixBillSteps");
                              }}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                    {getErrorMessage("suffixBillSteps")}

                    {renderCompletedError("suffixBillSteps", "day2")}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("bolCheck")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.bolCheck === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "bolCheck",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "bolCheckSteps",
                                  e.target.checked ? values.bolCheckSteps : "",
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Check the BOL to confirm completion and no
                        discrepancies? <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Use info on BOL in option 5 to look for duplicate Pros/billing errors. Be sure to check for how the BOL was signed."
                          tooltipKey="tooltip3"
                        />
                      </FormLabel>
                      {getErrorMessage("bolCheck")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.bolCheck === "Y" && (
                            <>
                              <TextField
                                autoComplete="off"
                                name="bolCheckSteps"
                                value={values.bolCheckSteps}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                error={hasError("bolCheckSteps")}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("bolCheckSteps");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />

                              {renderCompletedError("bolCheckSteps", "day2")}
                              {getErrorMessage("bolCheckSteps")}
                              <br />
                              <br />

                              <NoteBox message="Note: Search for PO #, BOL#, Shipper #, other Pro #’s, Driver signature & date, and any notation of shipper load & count." />
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="drCheck">
                        Is there a DR? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="drCheck"
                            name="drCheck"
                            className="textarea-radio-group"
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                              value: string
                            ) => {
                              onChangeRadio(event, value);
                            }}
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="drCheck"
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.drCheck === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="drCheck"
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.drCheck === "N"}
                            />
                          </RadioGroup>
                          {getErrorMessage("drCheck")}
                          {values.drCheck === "Y" && (
                            <>
                              <FormLabel id="drCheckStepsLabel">
                                Did you confirm completion and no discrepancies?{" "}
                                <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                id="drCheckSteps"
                                name="drCheckSteps"
                                fullWidth
                                value={values.drCheckSteps}
                                error={hasError("drCheckSteps")}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "drCheckSteps",
                                    "drCheck",
                                    STEP_STATUS_MAP[3]
                                  );
                                  isValueExist("drCheckSteps");
                                }}
                                disabled={formFieldsDisabled}
                                className="mt-1"
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("drCheckSteps")}

                          {renderCompletedError("drCheckSteps", "day2")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                  {/* F18 Notes */}
                  {renderF18Note("day2F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[4] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("pumCheck")}
                          onChange={(
                            _event: React.SyntheticEvent,
                            checked: boolean
                          ) => {
                            setFieldValue("pumCheck", checked ? "Y" : "N");
                            setFieldValue(
                              "pickupManifest",
                              checked ? values.pickupManifest : "",
                              true
                            );
                          }}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.pumCheck === "Y"}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Check the PUM (Pickup manifest) for discrepancies,
                        shipper load and count information, and proper reporting
                        process <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("pumCheck")}
                      {values.pumCheck === "Y" && (
                        <Grid container spacing={2}>
                          <Grid item md={4} xs={12} className="all-short-entry">
                            <FormLabel
                              id="pickupManifestLabel"
                              className="mt-0 mb-1"
                            >
                              Pickup Manifest # <sup>*</sup>
                            </FormLabel>
                            <TextField
                              autoComplete="off"
                              name="pickupManifest"
                              value={values.pickupManifest}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              type="number"
                              onPaste={handlePaste}
                              onKeyDown={handleKeyDown}
                              onInput={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleNumberMaxLength(e, 9);
                              }}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                                isValueExist("pickupManifest");
                              }}
                              error={
                                values.pickupManifest === 0 ||
                                hasError("pickupManifest")
                              }
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />

                            {getErrorMessage("pickupManifest")}
                            {renderCompletedError(
                              "pickupManifest",
                              "day3",
                              values.pickupManifest !== 0
                            )}

                            {values.pickupManifest === 0 && (
                              <span className="form-error">
                                Invalid pickup manifest#
                              </span>
                            )}
                          </Grid>
                          <Grid item md={8} xs={12} className="all-short-entry">
                            <FormLabel id="pumStepsL" className="mt-0 mb-1">
                              List any and all discrepancies <sup>*</sup>
                            </FormLabel>
                            <TextField
                              autoComplete="off"
                              name="pumSteps"
                              value={values.pumSteps}
                              error={hasError("pumSteps")}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                                isValueExist("pumSteps");
                              }}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                            {getErrorMessage("pumSteps")}
                            {renderCompletedError("pumSteps", "day3")}
                          </Grid>
                        </Grid>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="shipperLoad">
                        Was this a shipper load and count? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="shipperLoad"
                            name="shipperLoad"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="shipperLoad"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue(
                                  "discrepancies",
                                  initValues["discrepancies"],
                                  true
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.shipperLoad === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="shipperLoad"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue("discrepancies", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) => field !== "discrepancies"
                                  )
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.shipperLoad === "N"}
                            />
                          </RadioGroup>
                          {getErrorMessage("shipperLoad")}
                          {values.shipperLoad === "Y" && (
                            <>
                              <FormLabel
                                id="discrepanciesLabel"
                                className="mb-10"
                              >
                                Were there any discrepancies? If so, were they
                                properly reported to the shipper? <sup>*</sup>
                              </FormLabel>

                              <TextField
                                autoComplete="off"
                                name="discrepancies"
                                value={values.discrepancies}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                error={hasError("discrepancies")}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "discrepancies",
                                    "shipperLoad",
                                    STEP_STATUS_MAP[4]
                                  );
                                  isValueExist("discrepancies");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("discrepancies")}
                          {renderCompletedError("discrepancies", "day3")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    {renderCheckboxQuestion(
                      "checkLinehaulManifests",
                      "linehaulManifests",
                      <>Check all linehaul manifests – movement and abnormalities.</>,
                      {
                        text: "Get manifest numbers from the movement screen (F10)",
                        key: "tooltip4",
                      }
                    )}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    {renderCheckboxQuestion(
                      "interviewWorkers",
                      "workerSteps",
                      <>Interview the dock worker(s) about movement abnormalities.</>
                    )}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <h3 className="header-text mb-0">
                      Call consignee to confirm what was ordered and received
                      (if anything)
                    </h3>
                  </Grid>

                  {renderSimpleTextField(
                    "consigneePhone",
                    "Phone Number",
                    "Enter Phone Number",
                    12,
                    3,
                    "day3"
                  )}

                  {renderSimpleTextField(
                    "consigneeName",
                    "Consignee Name",
                    "Enter Consignee Name",
                    30,
                    3,
                    "day3"
                  )}

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="destinationTerminal">
                        Is the consignee still short? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="destinationTerminal"
                            name="destinationTerminal"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="consigneeShort"
                              // onChange={handleChange}
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue(
                                  "itemNumbers",
                                  initValues["itemNumbers"],
                                  true
                                );
                                setFieldValue(
                                  "orderReceived",
                                  initValues["orderReceived"],
                                  true
                                );
                                setFieldValue(
                                  "freightLabeled",
                                  initValues["freightLabeled"],
                                  true
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.consigneeShort === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="consigneeShort"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue("itemNumbers", "", true);
                                setFieldValue("orderReceived", "", true);
                                setFieldValue("freightLabeled", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) =>
                                      ![
                                        "itemNumbers",
                                        "orderReceived",
                                        "freightLabeled",
                                      ].includes(field)
                                  )
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.consigneeShort === "N"}
                            />
                          </RadioGroup>
                          {getErrorMessage("consigneeShort")}
                          {values.consigneeShort === "N" && (
                            <NoteBox message="Note: If the consignee is no longer short, the investigation is considered complete once we obtain written confirmation and/or a signed DR." />
                          )}

                          {values.consigneeShort === "Y" && (
                            <>
                              <FormLabel className="mb-10">
                                What are the item numbers received and still
                                missing? <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="itemNumbers"
                                value={values.itemNumbers}
                                error={hasError("itemNumbers")}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "itemNumbers",
                                    "consigneeShort",
                                    STEP_STATUS_MAP[4]
                                  );
                                  isValueExist("itemNumbers");
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("itemNumbers")}
                              {renderCompletedError("itemNumbers", "day3")}

                              <FormLabel className="mb-10">
                                What was ordered and received for each PO?{" "}
                                <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="orderReceived"
                                value={values.orderReceived}
                                error={hasError("orderReceived")}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "orderReceived",
                                    "consigneeShort",
                                    STEP_STATUS_MAP[4]
                                  );
                                  isValueExist("orderReceived");
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("orderReceived")}
                              {renderCompletedError("orderReceived", "day3")}

                              <FormLabel className="mb-10">
                                Was the freight received labeled for them?{" "}
                                <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="freightLabeled"
                                value={values.freightLabeled}
                                error={hasError("freightLabeled")}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "freightLabeled",
                                    "consigneeShort",
                                    STEP_STATUS_MAP[4]
                                  );
                                  isValueExist("freightLabeled");
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("freightLabeled")}
                              {renderCompletedError("freightLabeled", "day3")}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <h3 className="header-text mb-0">
                      Call Shipper to confirm what was shipped, how it was
                      packaged and the value?
                    </h3>
                  </Grid>

                  <Grid item {...S_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        Phone Number <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="shipperPhone"
                        value={values.shipperPhone}
                        error={hasError("shipperPhone")}
                        fullWidth
                        inputProps={{ maxLength: 12 }}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("shipperPhone");
                        }}
                        disabled={formFieldsDisabled}
                        variant="outlined"
                        placeholder="Enter Phone Number"
                      />
                    </FormControl>
                    {getErrorMessage("shipperPhone")}
                    {renderCompletedError("shipperPhone", "day3")}
                  </Grid>

                  <Grid item {...S_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        Shipper Name <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="shipperName"
                        value={values.shipperName}
                        error={hasError("shipperName")}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("shipperName");
                        }}
                        disabled={formFieldsDisabled}
                        placeholder="Enter Shipper Name"
                        inputProps={{ maxLength: 30 }}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("shipperName")}
                    {renderCompletedError("shipperName", "day3")}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        What are the details of what shipped? <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="shippedDetails"
                        value={values.shippedDetails}
                        error={hasError("shippedDetails")}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("shippedDetails");
                        }}
                        disabled={formFieldsDisabled}
                        fullWidth
                        placeholder="Describe Steps Taken"
                        inputProps={{ maxLength: MAX_LENGTH_SUMMARY_TEXT }}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("shippedDetails")}
                    {renderCompletedError("shippedDetails", "day3")}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        Was this short shipped? <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="shortShipped"
                        value={values.shortShipped}
                        error={hasError("shortShipped")}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("shortShipped");
                        }}
                        disabled={formFieldsDisabled}
                        fullWidth
                        placeholder="Describe Steps Taken"
                        inputProps={{ maxLength: MAX_LENGTH_SUMMARY_TEXT }}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("shortShipped")}
                    {renderCompletedError("shortShipped", "day3")}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        What is the value of the product? <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="productValue"
                        value={values.productValue}
                        error={hasError("productValue")}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("productValue");
                        }}
                        disabled={formFieldsDisabled}
                        fullWidth
                        placeholder="Describe Steps Taken"
                        inputProps={{ maxLength: MAX_LENGTH_SUMMARY_TEXT }}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("productValue")}
                    {renderCompletedError("productValue", "day3")}
                  </Grid>

                  <Grid item {...XL_GRID_PROPS} className="pt-0">
                    <FormControl className="radio-group question-group ">
                      <FormLabel className="mb-10">
                        Were there multiple shipments for the same consignee
                        that day? <sup>*</sup>
                      </FormLabel>
                      <TextField
                        autoComplete="off"
                        name="multShipments"
                        value={values.multShipments}
                        error={hasError("multShipments")}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          isValueExist("multShipments");
                        }}
                        disabled={formFieldsDisabled}
                        fullWidth
                        placeholder="Describe Steps Taken"
                        inputProps={{ maxLength: MAX_LENGTH_SUMMARY_TEXT }}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("multShipments")}
                    {renderCompletedError("multShipments", "day3")}
                  </Grid>

                  {/* F18 Notes */}
                  {renderF18Note("day3F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[5] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("check7818")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.check7818 === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "check7818",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "details7818",
                                  e.target.checked ? values.details7818 : "",
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Check 78/18 and/or Power BI for any overages (OV, NB,
                        DO) to apply <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("check7818")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.check7818 === "Y" && (
                            <TextField
                              autoComplete="off"
                              id={`details7818`}
                              name="details7818"
                              value={values.details7818}
                              error={hasError("details7818")}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                                isValueExist("details7818");
                              }}
                              disabled={formFieldsDisabled}
                              fullWidth
                              className=""
                              placeholder="Provide details of your search"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              variant="outlined"
                            />
                          )}
                          {getErrorMessage("details7818")}
                          {renderCompletedError("details7818", "day4")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="manifestedLable">
                        Did this show manifested to the destination terminal
                        (even if NF)? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="destinationTerminal"
                            name="manifested"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="manifested"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue(
                                  "cameraFindings",
                                  initValues["cameraFindings"],
                                  true
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.manifested === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="manifested"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                setFieldValue("cameraFindings", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) => field !== "cameraFindings"
                                  )
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.manifested === "N"}
                            />
                          </RadioGroup>
                          {getErrorMessage("manifested")}
                          {values.manifested === "Y" && (
                            <>
                              <FormLabel id="cameraFindingsLabel">
                                Check cameras at DT and report findings{" "}
                                <sup>*</sup> <sup>&nbsp;</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="cameraFindings"
                                value={values.cameraFindings}
                                error={hasError("cameraFindings")}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "cameraFindings",
                                    "manifested",
                                    STEP_STATUS_MAP[5]
                                  );
                                  isValueExist("cameraFindings");
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                className="mt-1"
                                placeholder="Describe Steps Taken"
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("cameraFindings")}
                          {renderCompletedError("cameraFindings", "day4")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("callSameDays")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.callSameDays === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const checked = e.target.checked;
                                setFieldValue(
                                  "callSameDays",
                                  checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "callSameDaysDataset",
                                  getCallSameDaysDataset(checked),
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Call same day deliveries and pickups <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Use F17 from FB inquiry screen for pickup info. To look up delivery manifest use option 16 from the operations menu"
                          tooltipKey="tooltip5"
                        />
                      </FormLabel>
                      {isCompleting && (
                        <span className="form-error">
                          {errors["callSameDays"] as string}
                        </span>
                      )}

                      {values.callSameDays === "Y" && (
                        <Grid
                          key={formKey}
                          container
                          spacing={2}
                          className="filter-margin"
                        >
                          <FieldArray name="callSameDaysDataset">
                            {({ push, remove }) => (
                              <>
                                {get(values, "callSameDaysDataset", []).map(
                                  (_d: ICallSameDaysDataset, index: number) => (
                                    <React.Fragment
                                      key={index + "" /* NOSONAR */}
                                    >
                                      <Grid
                                        item
                                        {...XL_GRID_PROPS}
                                        className="pt-0"
                                      >
                                        <h3 className="header-text mb-0">
                                          PRO and Delivery Info: {index + 1}
                                        </h3>
                                      </Grid>

                                      <Grid
                                        item
                                        {...S_GRID_PROPS}
                                        className="pt-0"
                                      >
                                        <FormControl className="radio-group question-group ">
                                          <FormLabel>
                                            All same Day Pro's <sup>*</sup>
                                          </FormLabel>
                                          <TextField
                                            autoComplete="off"
                                            id={`tid-${index}`}
                                            name={`callSameDaysDataset[${index}].sameDayPro`}
                                            value={get(
                                              values,
                                              `callSameDaysDataset[${index}].sameDayPro`,
                                              ""
                                            )}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                              handleBlur(e);
                                              isValueExist(
                                                "sameDayPro",
                                                index,
                                                "callSameDaysDataset"
                                              );
                                            }}
                                            disabled={formFieldsDisabled}
                                            fullWidth
                                            className="mt-1"
                                            placeholder="PRO Number"
                                            variant="outlined"
                                            type="text"
                                            onKeyDown={handleTextFieldKeyDown}
                                            inputProps={{ maxLength: 32 }}
                                          />
                                        </FormControl>

                                        {renderFormArrayError(
                                          index,
                                          "sameDayPro",
                                          "callSameDaysDataset"
                                        )}

                                        {Number.isNaN(
                                          +(
                                            values?.callSameDaysDataset?.[index]
                                              ?.sameDayPro ?? ""
                                          )
                                        ) &&
                                          values.callSameDays === "Y" && (
                                            <span className="form-error">
                                              Invalid PRO#
                                            </span>
                                          )}
                                        {renderCompletedError(
                                          "sameDayPro" + index,
                                          "day4",
                                          !values?.callSameDaysDataset?.[index]
                                            ?.sameDayPro
                                        )}
                                      </Grid>

                                      <Grid
                                        item
                                        {...L_GRID_PROPS}
                                        className="pt-0"
                                      >
                                        <FormControl className="radio-group question-group ">
                                          <FormLabel>
                                            Delivery Info?<sup>*</sup>
                                          </FormLabel>
                                          <TextField
                                            autoComplete="off"
                                            id={`deliveryInfo-${index}`}
                                            name={`callSameDaysDataset[${index}].deliveryInfo`}
                                            value={get(
                                              values,
                                              `callSameDaysDataset[${index}].deliveryInfo`,
                                              ""
                                            )}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                              handleBlur(e);
                                              isValueExist(
                                                "deliveryInfo",
                                                index,
                                                "callSameDaysDataset"
                                              );
                                            }}
                                            disabled={formFieldsDisabled}
                                            fullWidth
                                            className="mt-1"
                                            placeholder="Delivery Info"
                                            inputProps={{
                                              maxLength:
                                                MAX_LENGTH_SUMMARY_TEXT,
                                            }}
                                            variant="outlined"
                                          />
                                        </FormControl>
                                        {renderFormArrayError(
                                          index,
                                          "deliveryInfo",
                                          "callSameDaysDataset"
                                        )}

                                        {renderCompletedError(
                                          "deliveryInfo" + index,
                                          "day4",
                                          !values?.callSameDaysDataset?.[index]
                                            ?.deliveryInfo
                                        )}
                                      </Grid>
                                    </React.Fragment>
                                  )
                                )}
                                <Grid
                                  item
                                  {...XL_GRID_PROPS}
                                  className="btn-group-submit"
                                >
                                  {!(
                                    formFieldsDisabled ||
                                    status ===
                                      EShortageInvestigationStatus.complete
                                  ) && (
                                    <>
                                      {values?.callSameDaysDataset?.length! <
                                        24 && (
                                        <Button
                                          onClick={
                                            proInfoAddButtonDisabled
                                              ? void 0
                                              : () => {
                                                  push({
                                                    sameDayPro: undefined,
                                                    deliveryInfo: undefined,
                                                  });
                                                  proInfoAddRemove();
                                                }
                                          }
                                          variant="contained"
                                          className={`entry ml ${
                                            proInfoAddButtonDisabled
                                              ? "disable-btn disable"
                                              : ""
                                          }`}
                                        >
                                          Add
                                        </Button>
                                      )}
                                      {values.callSameDaysDataset?.length! >
                                        1 && (
                                        <Button
                                          onClick={() => {
                                            remove(
                                              values.callSameDaysDataset
                                                ?.length! - 1
                                            );
                                            proInfoAddRemove("remove");
                                          }}
                                          variant="contained"
                                          className="clear-btn mr-0 ml"
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </Grid>
                              </>
                            )}
                          </FieldArray>
                        </Grid>
                      )}
                    </FormControl>
                  </Grid>
                  {/* F18 Notes */}
                  {renderF18Note("day4F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[6] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("contactTerminals")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.contactTerminals === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const checked = e.target.checked;
                                setFieldValue(
                                  "contactTerminals",
                                  checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "freightTerminalLeadInquiries",
                                  getFreightTerminalLeadInquiries(checked),
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Contact the terminals that handled the freight to find
                        leads and check cameras if applicable * <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Use movement screen (F10) to locate terminals."
                          tooltipKey="tooltip6"
                        />
                      </FormLabel>
                      {getErrorMessage("contactTerminals")}
                    </FormControl>
                  </Grid>

                  {values.contactTerminals === "Y" && (
                    <Grid
                      key={formKey}
                      container
                      spacing={2}
                      className="filter-margin custom-container-pos"
                    >
                      <FieldArray name="freightTerminalLeadInquiries">
                        {({ push, remove }) => (
                          <>
                            {get(
                              values,
                              "freightTerminalLeadInquiries",
                              []
                            ).map((_i: IDay5Investigation, index: number) => (
                              <React.Fragment key={index + ""} /* NOSONAR */>
                                <Grid item {...XL_GRID_PROPS} className="pt-0">
                                  <h3 className="header-text mb-0">
                                    Investigation details: {index + 1}
                                  </h3>
                                </Grid>

                                <Grid item {...S_GRID_PROPS} className="pt-0">
                                  <FormControl className="radio-group question-group ">
                                    <FormLabel>
                                      TID <sup>*</sup>
                                    </FormLabel>
                                    <TextField
                                      autoComplete="off"
                                      id={`tid-${index}`}
                                      name={`freightTerminalLeadInquiries[${index}].terminal`}
                                      value={get(
                                        values,
                                        `freightTerminalLeadInquiries[${index}].terminal`
                                      )}
                                      onChange={(e) => {
                                        handleChange(e);
                                        isValueExist("terminal", index);
                                      }}
                                      onBlur={(e) => {
                                        handleBlur(e);
                                        isValueExist("terminal", index);
                                      }}
                                      disabled={formFieldsDisabled}
                                      fullWidth
                                      className="mt-1"
                                      placeholder="Enter TID"
                                      inputProps={{ maxLength: 5 }}
                                      variant="outlined"
                                      error={hasFreightTerminalError(
                                        index,
                                        "terminal"
                                      )}
                                    />
                                  </FormControl>
                                  {renderFormArrayError(index, "terminal")}

                                  {renderCompletedError(
                                    "terminal" + index,
                                    "day5",
                                    !values?.freightTerminalLeadInquiries?.[
                                      index
                                    ]?.terminal?.trim()
                                  )}
                                </Grid>

                                <Grid item {...S_GRID_PROPS} className="pt-0">
                                  <FormControl className="radio-group question-group ">
                                    <FormLabel>
                                      Manifest #<sup>*</sup>
                                    </FormLabel>
                                    <TextField
                                      autoComplete="off"
                                      id={`manifest-${index}`}
                                      type="number"
                                      onPaste={handlePaste}
                                      onKeyDown={handleKeyDown}
                                      onInput={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) => {
                                        handleNumberMaxLength(e, 9);
                                        isValueExist("manifest", index);
                                      }}
                                      name={`freightTerminalLeadInquiries[${index}].manifest`}
                                      value={get(
                                        values,
                                        `freightTerminalLeadInquiries[${index}].manifest`
                                      )}
                                      onChange={(e) => {
                                        handleChange(e);
                                        isValueExist("manifest", index);
                                      }}
                                      onBlur={(e) => {
                                        handleBlur(e);
                                        isValueExist("manifest", index);
                                      }}
                                      error={
                                        hasFreightTerminalError(
                                          index,
                                          "manifest"
                                        ) ||
                                        values?.freightTerminalLeadInquiries?.[
                                          index
                                        ]?.manifest === 0
                                      }
                                      disabled={formFieldsDisabled}
                                      fullWidth
                                      className="mt-1"
                                      placeholder="Enter Manifest #"
                                      inputProps={{ maxLength: 9 }}
                                      variant="outlined"
                                    />
                                  </FormControl>
                                  {values?.freightTerminalLeadInquiries![index]
                                    ?.manifest !== 0 &&
                                    renderFormArrayError(index, "manifest")}

                                  {renderCompletedError(
                                    "manifest" + index,
                                    "day5",
                                    !values?.freightTerminalLeadInquiries?.[
                                      index
                                    ]?.manifest &&
                                      values?.freightTerminalLeadInquiries![
                                        index
                                      ]?.manifest !== 0
                                  )}
                                  {values?.freightTerminalLeadInquiries![index]
                                    ?.manifest === 0 && (
                                    <span className="form-error">
                                      Invalid Manifest#
                                    </span>
                                  )}
                                </Grid>

                                <Grid item {...M_GRID_PROPS} className="pt-0">
                                  <FormControl className="radio-group question-group ">
                                    <FormLabel>
                                      List any discrepancies or abnormalities{" "}
                                      <sup>*</sup>
                                    </FormLabel>
                                    <TextField
                                      autoComplete="off"
                                      id={`pumSteps-${index}`}
                                      name={`freightTerminalLeadInquiries[${index}].discrepancies`}
                                      value={get(
                                        values,
                                        `freightTerminalLeadInquiries[${index}].discrepancies`
                                      )}
                                      onChange={(e) => {
                                        handleChange(e);
                                        isValueExist("discrepancies", index);
                                      }}
                                      error={hasFreightTerminalError(
                                        index,
                                        "discrepancies"
                                      )}
                                      onBlur={(e) => {
                                        handleBlur(e);
                                        isValueExist("discrepancies", index);
                                      }}
                                      disabled={formFieldsDisabled}
                                      fullWidth
                                      className="mt-1"
                                      placeholder="List all terminals contacted and your findings"
                                      inputProps={{
                                        maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                      }}
                                      variant="outlined"
                                    />
                                  </FormControl>
                                  {renderFormArrayError(index, "discrepancies")}
                                  {renderCompletedError(
                                    "discrepancies" + index,
                                    "day5",
                                    !values?.freightTerminalLeadInquiries?.[
                                      index
                                    ]?.discrepancies?.trim()
                                  )}
                                </Grid>
                              </React.Fragment>
                            ))}
                            <Grid
                              item
                              {...XL_GRID_PROPS}
                              className="btn-group-submit"
                            >
                              {!(
                                formFieldsDisabled ||
                                status === EShortageInvestigationStatus.complete
                              ) && (
                                <>
                                  {values.freightTerminalLeadInquiries
                                    ?.length! < 6 && (
                                    <Button
                                      onClick={
                                        addButtonDisabled
                                          ? void 0
                                          : () => {
                                              push({
                                                terminal: undefined,
                                                manifest: undefined,
                                                discrepancies: undefined,
                                              });
                                              addRemove();
                                            }
                                      }
                                      variant="contained"
                                      className={`entry ml ${
                                        addButtonDisabled
                                          ? "disable-btn disable"
                                          : ""
                                      }`}
                                    >
                                      Add
                                    </Button>
                                  )}
                                  {(values.freightTerminalLeadInquiries
                                    ?.length ?? 0) > 1 && (
                                    <Button
                                      onClick={() => {
                                        remove(
                                          values.freightTerminalLeadInquiries
                                            ?.length! - 1
                                        );
                                        addRemove("remove");
                                      }}
                                      variant="contained"
                                      className="clear-btn mr-0 ml"
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </>
                              )}
                            </Grid>
                          </>
                        )}
                      </FieldArray>
                    </Grid>
                  )}
                  {/* F18 Notes */}
                  {renderF18Note("day5F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[7] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("followUp")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.followUp === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "followUp",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "followUpDetails",
                                  e.target.checked
                                    ? values.followUpDetails
                                    : "",
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />{" "}
                        Follow up on all leads/request from previous days{" "}
                        <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("followUp")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.followUp === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="followUpDetails"
                              value={values.followUpDetails}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              disabled={formFieldsDisabled}
                              fullWidth
                              placeholder="Provide details of your search"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                  {/* F18 Notes */}
                  {renderF18Note("day10F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[8] && (
              <Day15Investigation
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                values={values}
                setFieldValue={setFieldValue}
                osdNumber={investigationDetails?.osdNumber}
                exceptionType={EOsdType.as}
                isControlDisabled={isControlDisabled}
                formFieldsDisabled={formFieldsDisabled}
                getErrorMessage={getErrorMessage}
                isValueExist={isValueExist}
                handleChange={handleChange}
                handleBlur={handleBlur}
                renderF18Note={renderF18Note}
                renderCompletedError={renderCompletedError}
                hasError={hasError}
                initValues={initValues}
                activeStep={activeStep}
                setIsShowSendAPB={setIsShowSendAPB}
              />
            )}

            {activeStep === STEP_STATUS_MAP[9] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...XL_GRID_PROPS}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={isFieldDisabled("finalReCheck")}
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.finalReCheck === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "finalReCheck",
                                  e.target.checked ? "Y" : "N"
                                );
                                setFieldValue(
                                  "detailsOfSearch",
                                  e.target.checked
                                    ? initValues.detailsOfSearch
                                    : "",
                                  true
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Final re-check of all leads/requests/overages to confirm
                        the freight cannot be located at this time <sup>*</sup>
                      </FormLabel>
                      {getErrorMessage("finalReCheck")}
                      <Grid container spacing={2}>
                        <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                          {values.finalReCheck === "Y" && (
                            <TextField
                              autoComplete="off"
                              id={`detailsOfSearch`}
                              name="detailsOfSearch"
                              value={values.detailsOfSearch ?? ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              fullWidth
                              className=""
                              placeholder="Provide details of your search"
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                  {/* F18 Notes */}
                  {renderF18Note("day20F18Note")}
                </Grid>
              </div>
            )}
          </>
        </FormikProvider>
        {!isCleared && 
        <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
          {formFieldsDisabled &&
            status !== EShortageInvestigationStatus.complete && (
              <Button
                variant="contained"
                onClick={enableEditing}
                className="assign ml mr-0"
              >
                Edit
              </Button>
            )}
          {!formFieldsDisabled && (
            <>
              <Button
                onClick={() => reset()}
                variant="contained"
                className="clear-btn mr-0 ml"
                type="button"
              >
                {isEditMode ? "Cancel" : "Clear"}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  saveOrSubmit(EFormAction.save);
                }}
                className={saveCompleteBtnClassName()}
              >
                Save
              </Button>
              {displayCompleteBtn && (
                <Button
                  variant="contained"
                  onClick={() => {
                    saveOrSubmit(EFormAction.submit);
                  }}
                  className={saveCompleteBtnClassName()}
                >
                  Complete Day {activeDay}
                </Button>
              )}
            </>
          )}
          {formFieldsDisabled &&
            !isFreightLocated &&
            STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] ===
              activeStep &&
            ![
              EShortageInvestigationStatus.complete,
              EShortageInvestigationStatus.exceptionEntry,
              EShortageInvestigationStatus.pendingSrApproval,
            ].includes(status) && (
              <Button
                onClick={() => handleDialogOpen()}
                variant="contained"
                className="entry ml"
              >
                Freight Located
              </Button>
            )}

          {shouldShowCompleteButton && (
            <Button
              onClick={() => handleFreightAndCompletion(EFormAction.complete)}
              variant="contained"
              className="entry ml"
            >
              Complete & Image
            </Button>
          )}
        </Grid>
        }
      </AccordionDetails>
      <Loader pageLoader={pageLoading}></Loader>

      {AS_INVESTIGATION_WIZARD_STEPS.map((day: string, index: number) => (
        <>
          {day === "Day 4" ? (
             <>
              <div key={day} className="hide-me" id={`investigation-${day}`}>
                <ASInvestigationTemplate
                  currentDay={day}
                  values={values}
                  osdNumber={investigationDetails?.osdNumber}
                />
              </div>
              {(callSameDaysChunks || [[]]).map(
                (chunk: ICallSameDaysDataset[], index: number) => {
                  const chunkId = `investigation-${day}-callSameDays-${index}`;
                  return (
                    <div key={chunkId} className="hide-me" id={chunkId}>
                      <ASDay4Template
                        currentDay={day}
                        osdNumber={investigationDetails?.osdNumber}
                        investigationValues={values}
                        callSameDaysChunk={chunk}
                        pageIndex={getPageIndex(index, callSameDaysChunks)}
                      />
                    </div>
                  );
                }
              )}
            </>
          ) : (
            <div key={day} className="hide-me" id={`investigation-${day}`}>
              <ASInvestigationTemplate
                currentDay={day}
                values={values}
                osdNumber={investigationDetails?.osdNumber}
              />
            </div>
          )}
        </>
      ))}
      <CustomDialog
        message={"Are you sure freight is located?"}
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </div>
  );
};
export default React.memo(ShortageInvestigation);
